/**
 * Performance Monitoring Component
 * Tracks component render times, user interactions, and application performance
 */

import React, { useEffect, useRef } from 'react'

// Performance metrics collector
class PerformanceCollector {
  constructor() {
    this.metrics = []
    this.isEnabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true'
  }

  // Record a performance metric
  record(metric) {
    if (!this.isEnabled) return

    const timestamp = Date.now()
    this.metrics.push({
      ...metric,
      timestamp,
      sessionId: this.getSessionId()
    })

    // Send to analytics service if buffer is full
    if (this.metrics.length >= 10) {
      this.flush()
    }
  }

  // Send metrics to analytics service
  flush() {
    if (!this.isEnabled || this.metrics.length === 0) return

    try {
      // In production, send to analytics service
      if (process.env.NODE_ENV === 'production') {
        // Example: Send to analytics endpoint
        // fetch('/api/analytics/performance', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(this.metrics)
        // })
        
        console.log('Performance metrics (would send to analytics):', this.metrics)
      } else {
        console.group('Performance Metrics')
        this.metrics.forEach(metric => {
          console.log(`${metric.type}: ${metric.name}`, metric)
        })
        console.groupEnd()
      }

      this.metrics = []
    } catch (error) {
      console.error('Failed to send performance metrics:', error)
    }
  }

  // Get or create session ID
  getSessionId() {
    if (!this.sessionId) {
      this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }
    return this.sessionId
  }

  // Record page load performance
  recordPageLoad(pageName) {
    if (typeof window !== 'undefined' && window.performance) {
      const navigation = window.performance.getEntriesByType('navigation')[0]
      if (navigation) {
        this.record({
          type: 'page_load',
          name: pageName,
          duration: navigation.loadEventEnd - navigation.loadEventStart,
          domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          firstPaint: this.getFirstPaint(),
          largestContentfulPaint: this.getLCP()
        })
      }
    }
  }

  // Record component render time
  recordRender(componentName, renderTime) {
    this.record({
      type: 'component_render',
      name: componentName,
      duration: renderTime
    })
  }

  // Record user interaction
  recordInteraction(action, element, duration) {
    this.record({
      type: 'user_interaction',
      name: action,
      element,
      duration
    })
  }

  // Record API call performance
  recordApiCall(endpoint, method, duration, status) {
    this.record({
      type: 'api_call',
      name: `${method} ${endpoint}`,
      duration,
      status
    })
  }

  // Get First Paint timing
  getFirstPaint() {
    const paintEntries = window.performance.getEntriesByType('paint')
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint')
    return firstPaint ? firstPaint.startTime : null
  }

  // Get Largest Contentful Paint
  getLCP() {
    return new Promise((resolve) => {
      if ('LargestContentfulPaint' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          resolve(lastEntry.startTime)
          observer.disconnect()
        })
        observer.observe({ entryTypes: ['largest-contentful-paint'] })
      } else {
        resolve(null)
      }
    })
  }
}

// Global performance collector instance
const performanceCollector = new PerformanceCollector()

// Performance monitoring hook
export const usePerformanceMonitor = (componentName) => {
  const renderStartTime = useRef(Date.now())
  const mountTime = useRef(Date.now())

  useEffect(() => {
    // Record component mount time
    const mountDuration = Date.now() - mountTime.current
    performanceCollector.recordRender(`${componentName}_mount`, mountDuration)

    return () => {
      // Record component unmount (cleanup)
      performanceCollector.record({
        type: 'component_unmount',
        name: componentName,
        lifespan: Date.now() - mountTime.current
      })
    }
  }, [componentName])

  useEffect(() => {
    // Record render time after each update
    const renderDuration = Date.now() - renderStartTime.current
    if (renderDuration > 1) { // Only record renders that take more than 1ms
      performanceCollector.recordRender(componentName, renderDuration)
    }
    renderStartTime.current = Date.now()
  })

  // Return function to record custom metrics
  const recordCustomMetric = (metricType, metricName, value) => {
    performanceCollector.record({
      type: metricType,
      name: metricName,
      value,
      component: componentName
    })
  }

  return { recordCustomMetric }
}

// Performance Monitor Component
const PerformanceMonitor = ({ children, componentName = 'App' }) => {
  usePerformanceMonitor(componentName)

  useEffect(() => {
    // Record initial page load
    performanceCollector.recordPageLoad(componentName)

    // Set up automatic metric flushing
    const flushInterval = setInterval(() => {
      performanceCollector.flush()
    }, 30000) // Flush every 30 seconds

    // Flush on page unload
    const handleBeforeUnload = () => {
      performanceCollector.flush()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          performanceCollector.record({
            type: 'long_task',
            name: 'blocking_task',
            duration: entry.duration,
            startTime: entry.startTime
          })
        })
      })

      try {
        longTaskObserver.observe({ entryTypes: ['longtask'] })
      } catch (error) {
        console.warn('Long task monitoring not supported')
      }

      return () => {
        longTaskObserver.disconnect()
      }
    }

    return () => {
      clearInterval(flushInterval)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [componentName])

  return children
}

// HOC for performance monitoring
export const withPerformanceMonitoring = (Component, componentName) => {
  const WrappedComponent = (props) => {
    usePerformanceMonitor(componentName || Component.displayName || Component.name)
    return <Component {...props} />
  }

  WrappedComponent.displayName = `withPerformanceMonitoring(${componentName || Component.displayName || Component.name})`
  
  return WrappedComponent
}

// User interaction tracking hook
export const useInteractionTracking = () => {
  const recordInteraction = (action, element, additionalData = {}) => {
    const startTime = Date.now()
    
    return {
      // Call this when the interaction completes
      complete: () => {
        const duration = Date.now() - startTime
        performanceCollector.recordInteraction(action, element, duration)
        performanceCollector.record({
          type: 'interaction_complete',
          name: action,
          element,
          duration,
          ...additionalData
        })
      }
    }
  }

  return { recordInteraction }
}

// API performance tracking hook
export const useApiPerformanceTracking = () => {
  const trackApiCall = async (apiCall, endpoint, method = 'GET') => {
    const startTime = Date.now()
    let status = 'success'
    
    try {
      const result = await apiCall()
      return result
    } catch (error) {
      status = 'error'
      throw error
    } finally {
      const duration = Date.now() - startTime
      performanceCollector.recordApiCall(endpoint, method, duration, status)
    }
  }

  return { trackApiCall }
}

// Memory usage monitoring
export const useMemoryMonitor = () => {
  useEffect(() => {
    const monitorMemory = () => {
      if ('memory' in performance) {
        performanceCollector.record({
          type: 'memory_usage',
          name: 'heap_usage',
          usedJSHeapSize: performance.memory.usedJSHeapSize,
          totalJSHeapSize: performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
        })
      }
    }

    // Monitor memory every 60 seconds
    const memoryInterval = setInterval(monitorMemory, 60000)
    
    // Initial measurement
    monitorMemory()

    return () => clearInterval(memoryInterval)
  }, [])
}

// Export the main component and utilities
export default PerformanceMonitor
export { performanceCollector }